import { FileCode2, FlaskConical, Lock, Pencil, Plus, X } from "lucide-react";

import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import { useTheme } from "@mui/material";
import ButtonBase from "@mui/material/ButtonBase";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";

const FILE_LIST_WIDTH = 192;

/** One row of the tree. */
export interface WorkspaceFileListEntry {
  /** The row's filename, and the value handed back to {@link onSelect}. */
  name: string;
  /** Locked rows draw a padlock and never offer rename/delete. */
  isLocked: boolean;
  /** `true` when this row may be renamed or deleted. */
  canManage?: boolean;
  /** `"test"` draws a test-file icon on an unlocked row. Defaults to code. */
  kind?: "code" | "test";
  /** A short trailing marker, e.g. who wrote the file. */
  tag?: string;
}

/** A labelled group of rows below the main list, e.g. "Tests". */
export interface WorkspaceFileListSection {
  label: string;
  files: readonly WorkspaceFileListEntry[];
}

export interface WorkspaceFileListProps {
  files: readonly WorkspaceFileListEntry[];
  activeFile: string;
  onSelect: (name: string) => void;
  /** Omit to hide the "Add file" action. */
  onAdd?: () => void;
  /** Omit for a tree whose rows are never renamed or deleted. */
  onRename?: (from: string, to: string) => void;
  onDelete?: (name: string) => void;
  /** Groups rendered after the main rows and the "Add file" action. */
  sections?: readonly WorkspaceFileListSection[];
}

/**
 * The file tree: a vertical list left of the editor.
 *
 * Purely presentational — the caller decides which rows exist, in which order,
 * and which of them can be managed. That is what lets the code workspace (a
 * locked contract file, the entry file, user helpers) and the template
 * workspace (one editable config file over the template's read-only
 * implementation source) render the same list without sharing a view model.
 */
export const WorkspaceFileList = ({
  files,
  activeFile,
  onSelect,
  onAdd,
  onRename,
  onDelete,
  sections = [],
}: WorkspaceFileListProps) => {
  const theme = useTheme();

  return (
    <Stack
      spacing={0.25}
      sx={{
        width: FILE_LIST_WIDTH,
        flexShrink: 0,
        p: 0.75,
        borderRight: `1px solid ${theme.igBorders.core.default}`,
        overflowY: "auto",
      }}
    >
      {files.map((file) => (
        <FileRow
          key={file.name}
          file={file}
          isActive={activeFile === file.name}
          onSelect={onSelect}
          onRename={onRename}
          onDelete={onDelete}
        />
      ))}
      {onAdd ? (
        <ButtonBase
          onClick={onAdd}
          aria-label="Add file"
          sx={{
            justifyContent: "flex-start",
            gap: 0.75,
            px: 1,
            py: 0.5,
            borderRadius: 1,
            color: theme.palette.text.secondary,
          }}
        >
          <Plus size={13} />
          <IgTypography variant="body2" color="text.secondary">
            Add file
          </IgTypography>
        </ButtonBase>
      ) : null}
      {sections.map((section) => (
        <Stack
          key={section.label}
          component="section"
          aria-label={section.label}
          spacing={0.25}
          sx={{ pt: 1.5 }}
        >
          <IgTypography
            variant="caption"
            color="text.secondary"
            fontWeight={600}
            sx={{ px: 1, pb: 0.25 }}
          >
            {section.label}
          </IgTypography>
          {section.files.map((file) => (
            <FileRow
              key={file.name}
              file={file}
              isActive={activeFile === file.name}
              onSelect={onSelect}
              onRename={onRename}
              onDelete={onDelete}
            />
          ))}
        </Stack>
      ))}
    </Stack>
  );
};

/**
 * A single file row. Locked rows and rows the caller did not mark manageable
 * render without rename/delete actions.
 */
const FileRow = ({
  file,
  isActive,
  onSelect,
  onRename,
  onDelete,
}: {
  file: WorkspaceFileListEntry;
  isActive: boolean;
  onSelect: (name: string) => void;
  onRename?: (from: string, to: string) => void;
  onDelete?: (name: string) => void;
}) => {
  const theme = useTheme();
  const canManage =
    Boolean(file.canManage) && !file.isLocked && Boolean(onRename ?? onDelete);

  const handleRename = () => {
    const next = window.prompt(`Rename "${file.name}" to:`, file.name);
    if (next && next !== file.name) {
      onRename?.(file.name, next);
    }
  };

  const handleDelete = () => {
    if (window.confirm(`Delete "${file.name}"? This cannot be undone.`)) {
      onDelete?.(file.name);
    }
  };

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={0.5}
      sx={{
        px: 1,
        py: 0.5,
        borderRadius: 1,
        bgcolor: isActive ? "background.default" : "transparent",
        "&:hover": { bgcolor: "background.default" },
        // Reveal the manage actions on hover/focus only, so the list stays a
        // quiet file tree rather than a row of buttons. Opacity rather than
        // visibility keeps them in the accessibility tree for keyboard users.
        "&:hover .file-row-actions, &:focus-within .file-row-actions": {
          opacity: 1,
        },
      }}
    >
      <Stack
        component="button"
        type="button"
        direction="row"
        alignItems="center"
        spacing={0.75}
        onClick={() => onSelect(file.name)}
        aria-label={`Open ${file.name}`}
        aria-pressed={isActive}
        sx={{
          flex: 1,
          minWidth: 0,
          border: 0,
          background: "none",
          p: 0,
          cursor: "pointer",
          color: "inherit",
          textAlign: "left",
        }}
      >
        {file.isLocked ? (
          <Lock size={13} color={theme.palette.text.tertiary} />
        ) : file.kind === "test" ? (
          <FlaskConical size={13} color={theme.palette.text.accent} />
        ) : (
          <FileCode2 size={13} color={theme.palette.text.accent} />
        )}
        <IgTypography
          variant="body2"
          fontWeight={isActive ? 700 : 500}
          color={file.isLocked ? "text.secondary" : "text.primary"}
          noWrap
        >
          {file.name}
        </IgTypography>
        {file.tag ? (
          <IgTypography
            variant="caption"
            color="text.secondary"
            sx={{ flexShrink: 0 }}
          >
            {file.tag}
          </IgTypography>
        ) : null}
      </Stack>
      {canManage ? (
        <Stack
          className="file-row-actions"
          direction="row"
          alignItems="center"
          sx={{ opacity: 0 }}
        >
          <IconButton
            size="small"
            aria-label={`Rename ${file.name}`}
            onClick={handleRename}
            sx={{ p: 0.25 }}
          >
            <Pencil size={12} />
          </IconButton>
          <IconButton
            size="small"
            aria-label={`Delete ${file.name}`}
            onClick={handleDelete}
            sx={{ p: 0.25 }}
          >
            <X size={12} />
          </IconButton>
        </Stack>
      ) : null}
    </Stack>
  );
};
